import React, { useState } from 'react';
import { Checkbox, Input, InputNumber, Select, Form, Radio, Slider, Popover, Button, Space, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Configuration for parametrable field types
 * Each key is a field type that requires runtime configuration
 * 
 * Scale supports two modes:
 * - Numeric: min/max/step with optional showMarks
 * - Custom marks: array of { position, label, value } for ratings/hybrid scales
 *   Example: [{ position: 0, label: 'Poor', value: 'poor' }, { position: 100, label: 'Excellent', value: 'excellent' }]
 */
const PARAMETRABLE_FIELD_CONFIG = {
  scale: {
    defaults: { min: 0, max: 10, step: 1, showMarks: true, minLabel: '', maxLabel: '', useCustomMarks: false, customMarks: [], maxPosition: 100 },
    renderSettings: (config, onChange) => (
      <Space direction="vertical" size={8} style={{ width: 280 }}>
        <Text strong style={{ fontSize: 12 }}>Scale Settings</Text>
        <Checkbox 
          checked={config.useCustomMarks || false} 
          onChange={(e) => onChange({ 
            ...config, 
            useCustomMarks: e.target.checked,
            customMarks: e.target.checked && (!config.customMarks || config.customMarks.length === 0) 
              ? [{ position: 0, label: 'Min', value: '0' }, { position: config.maxPosition || 100, label: 'Max', value: String(config.maxPosition || 100) }] 
              : config.customMarks 
          })}
          style={{ fontSize: 12 }}
        >
          Use custom marks (ratings/hybrid)
        </Checkbox>
        {!config.useCustomMarks ? (
          <>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Min:</span>
              <InputNumber size="small" value={config.min} onChange={(v) => onChange({ ...config, min: v })} style={{ width: 60 }} />
              <Input size="small" value={config.minLabel || ''} onChange={(e) => onChange({ ...config, minLabel: e.target.value })} placeholder="Label" style={{ width: 70 }} />
            </Space>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Max:</span>
              <InputNumber size="small" value={config.max} onChange={(v) => onChange({ ...config, max: v })} style={{ width: 60 }} />
              <Input size="small" value={config.maxLabel || ''} onChange={(e) => onChange({ ...config, maxLabel: e.target.value })} placeholder="Label" style={{ width: 70 }} />
            </Space>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Step:</span>
              <InputNumber size="small" value={config.step} min={1} onChange={(v) => onChange({ ...config, step: v })} style={{ width: 60 }} />
            </Space>
            <Checkbox 
              checked={config.showMarks} 
              onChange={(e) => onChange({ ...config, showMarks: e.target.checked })}
              style={{ fontSize: 12 }}
            >
              Show marks
            </Checkbox>
          </>
        ) : (
          <>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Max Position:</span>
              <InputNumber size="small" value={config.maxPosition || 100} min={1} onChange={(v) => onChange({ ...config, maxPosition: v })} style={{ width: 60 }} />
            </Space>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Step:</span>
              <InputNumber size="small" value={config.step || 1} min={1} onChange={(v) => onChange({ ...config, step: v })} style={{ width: 60 }} />
            </Space>
            <ScaleCustomMarksEditor marks={config.customMarks || []} maxPosition={config.maxPosition || 100} onChange={(marks) => onChange({ ...config, customMarks: marks })} />
          </>
        )}
      </Space>
    )
  }
};

/**
 * Editor for custom scale marks (used in settings popover)
 */
const ScaleCustomMarksEditor = ({ marks, maxPosition = 100, onChange }) => {
  const addMark = () => {
    const newPos = marks.length > 0 ? Math.min(maxPosition, Math.max(...marks.map(m => m.position)) + 25) : 0;
    onChange([...marks, { position: newPos, label: '', value: '' }]);
  };
  
  const updateMark = (index, field, val) => {
    const updated = [...marks];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };
  
  const removeMark = (index) => {
    onChange(marks.filter((_, i) => i !== index));
  };

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 11 }}>Position (0-{maxPosition}) | Label | Output</Text>
      {marks.map((mark, i) => (
        <Space key={i} size={4}>
          <InputNumber size="small" value={mark.position} min={0} max={maxPosition} onChange={(v) => updateMark(i, 'position', v)} style={{ width: 50 }} placeholder="Pos" />
          <Input size="small" value={mark.label} onChange={(e) => updateMark(i, 'label', e.target.value)} style={{ width: 70 }} placeholder="Label" />
          <Input size="small" value={mark.value} onChange={(e) => updateMark(i, 'value', e.target.value)} style={{ width: 70 }} placeholder="Value" />
          <Button type="text" size="small" danger onClick={() => removeMark(i)} style={{ padding: '0 4px' }}>×</Button>
        </Space>
      ))}
      <Button type="dashed" size="small" onClick={addMark} block style={{ fontSize: 11 }}>+ Add Mark</Button>
    </Space>
  );
};

/**
 * Check if a field type is parametrable
 */
const isParametrableType = (type) => type in PARAMETRABLE_FIELD_CONFIG;

/**
 * Get merged config for a parametrable field (field config + defaults)
 */
const getParametrableConfig = (field) => {
  const typeConfig = PARAMETRABLE_FIELD_CONFIG[field.type];
  if (!typeConfig) return null;
  return { ...typeConfig.defaults, ...field.config };
};

/**
 * Dynamic Form Field Component
 * Renders different field types based on field configuration
 */
const FormField = ({ field, value, onChange, disabled = false, onConfigChange }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleChange = (newValue) => {
    onChange(field.id, newValue);
  };

  // Handle config changes for parametrable fields
  const handleConfigChange = (newConfig) => {
    if (onConfigChange) {
      onConfigChange(field.id, newConfig);
    }
  };

  // Render settings icon for parametrable field types (reusable pattern)
  const renderSettingsIcon = () => {
    if (!isParametrableType(field.type) || !onConfigChange) return null;
    
    const currentConfig = getParametrableConfig(field);
    const typeConfig = PARAMETRABLE_FIELD_CONFIG[field.type];
    
    return (
      <Popover
        content={typeConfig.renderSettings(currentConfig, handleConfigChange)}
        trigger="click"
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        placement="bottomRight"
      >
        <Button 
          type="text" 
          size="small" 
          icon={<SettingOutlined />} 
          style={{ marginLeft: 4, color: '#8c8c8c' }}
        />
      </Popover>
    );
  };

  const renderField = () => {
    switch (field.type) {
      case 'checkbox':
        if (field.allowOther) {
          // For "Other" checkbox: value is { checked: boolean, otherValue: string }
          const checkboxValue = typeof value === 'object' && value !== null 
            ? { checked: value.checked || false, otherValue: value.otherValue || '' }
            : { checked: value || false, otherValue: '' };
          
          return (
            <div>
              <Checkbox
                checked={checkboxValue.checked || false}
                onChange={(e) => {
                  const newValue = {
                    checked: e.target.checked,
                    otherValue: e.target.checked ? checkboxValue.otherValue : ''
                  };
                  handleChange(newValue);
                }}
                disabled={disabled}
              >
                {field.otherLabel || 'Other'}
              </Checkbox>
              {checkboxValue.checked && (
                <Input
                  placeholder="Please specify"
                  value={checkboxValue.otherValue || ''}
                  onChange={(e) => handleChange({ 
                    ...checkboxValue, 
                    otherValue: e.target.value 
                  })}
                  disabled={disabled}
                  style={{ marginTop: 8, marginLeft: 24 }}
                />
              )}
            </div>
          );
        }
        return (
          <Checkbox
            checked={value || false}
            onChange={(e) => handleChange(e.target.checked)}
            disabled={disabled}
          >
            {field.label}
          </Checkbox>
        );

      case 'checkbox-with-text':
        return (
          <div>
            <Checkbox
              checked={value?.checked || false}
              onChange={(e) => handleChange({ ...value, checked: e.target.checked })}
              disabled={disabled}
            >
              {field.label}
            </Checkbox>
            {value?.checked && (
              <Input
                placeholder={field.textFieldLabel || 'Please specify'}
                value={value?.text || ''}
                onChange={(e) => handleChange({ ...value, text: e.target.value })}
                disabled={disabled}
                style={{ marginTop: 8 }}
              />
            )}
          </div>
        );

      case 'dropdown':
      case 'select':
        const isOtherSelected = typeof value === 'object' && value !== null && value?.value === '__other__';
        const selectValue = typeof value === 'object' && value !== null ? value?.value : value;
        const otherValue = typeof value === 'object' && value !== null ? (value?.otherValue || '') : '';
        
        return (
          <Form.Item label={field.label}>
            <Select
              value={isOtherSelected ? '__other__' : (selectValue || undefined)}
              onChange={(selectedValue) => {
                if (selectedValue === '__other__') {
                  handleChange({ value: '__other__', otherValue: '' });
                } else {
                  handleChange(selectedValue);
                }
              }}
              placeholder={`Select ${field.label}`}
              disabled={disabled}
              allowClear
            >
              {field.options?.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
              {field.allowOther && (
                <Select.Option value="__other__">
                  {field.otherLabel || 'Other'}
                </Select.Option>
              )}
            </Select>
            {isOtherSelected && (
              <Input
                placeholder="Please specify"
                value={otherValue}
                onChange={(e) => handleChange({ value: '__other__', otherValue: e.target.value })}
                disabled={disabled}
                style={{ marginTop: 8 }}
              />
            )}
          </Form.Item>
        );

      case 'radio':
        const isRadioOtherSelected = typeof value === 'object' && value !== null && value?.value === '__other__';
        const radioValue = typeof value === 'object' && value !== null ? value?.value : value;
        const radioOtherValue = typeof value === 'object' && value !== null ? (value?.otherValue || '') : '';
        
        return (
          <Form.Item label={field.label}>
            <Radio.Group
              value={isRadioOtherSelected ? '__other__' : (radioValue || undefined)}
              onChange={(e) => {
                const selectedValue = e.target.value;
                if (selectedValue === '__other__') {
                  handleChange({ value: '__other__', otherValue: '' });
                } else {
                  handleChange(selectedValue);
                }
              }}
              disabled={disabled}
            >
              {field.options?.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
              {field.allowOther && (
                <Radio value="__other__">
                  {field.otherLabel || 'Other'}
                </Radio>
              )}
            </Radio.Group>
            {isRadioOtherSelected && (
              <Input
                placeholder="Please specify"
                value={radioOtherValue}
                onChange={(e) => handleChange({ value: '__other__', otherValue: e.target.value })}
                disabled={disabled}
                style={{ marginTop: 8 }}
              />
            )}
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item label={field.label}>
            <InputNumber
              value={value || field.defaultValue || 0}
              onChange={handleChange}
              disabled={disabled || field.calculated}
              min={field.min || 0}
              max={field.max}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'text':
        return (
          <Form.Item label={field.label}>
            <Input
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={field.placeholder || field.label}
              disabled={disabled}
            />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item label={field.label}>
            <TextArea
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={field.placeholder || field.label}
              disabled={disabled}
              rows={field.rows || 4}
            />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item label={field.label}>
            <Input
              type="date"
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              disabled={disabled}
            />
          </Form.Item>
        );

      case 'scale': {
        const config = getParametrableConfig(field);
        
        // Custom marks mode
        if (config.useCustomMarks && config.customMarks?.length > 0) {
          const sortedMarks = [...config.customMarks].sort((a, b) => a.position - b.position);
          const marks = {};
          sortedMarks.forEach(m => { marks[m.position] = m.label; });
          const step = config.step || 1;
          const maxPos = config.maxPosition || 100;
          
          // Get value from position (interpolate numeric ranges)
          const getValue = (pos) => {
            const exact = sortedMarks.find(m => m.position === pos);
            if (exact) return exact.value;
            for (let i = 0; i < sortedMarks.length - 1; i++) {
              const lo = sortedMarks[i], hi = sortedMarks[i + 1];
              if (pos > lo.position && pos < hi.position) {
                const loNum = parseFloat(lo.value), hiNum = parseFloat(hi.value);
                if (!isNaN(loNum) && !isNaN(hiNum)) {
                  const ratio = (pos - lo.position) / (hi.position - lo.position);
                  return Math.round((loNum + (hiNum - loNum) * ratio) / step) * step;
                }
                return lo.value;
              }
            }
            return sortedMarks[0]?.value;
          };
          
          // Get position from value (reverse lookup)
          const getPos = (val) => {
            const exact = sortedMarks.find(m => String(m.value) === String(val));
            if (exact) return exact.position;
            const num = parseFloat(val);
            if (!isNaN(num)) {
              for (let i = 0; i < sortedMarks.length - 1; i++) {
                const lo = sortedMarks[i], hi = sortedMarks[i + 1];
                const loNum = parseFloat(lo.value), hiNum = parseFloat(hi.value);
                if (!isNaN(loNum) && !isNaN(hiNum) && num >= loNum && num <= hiNum) {
                  return lo.position + (hi.position - lo.position) * ((num - loNum) / (hiNum - loNum));
                }
              }
            }
            return sortedMarks[0]?.position || 0;
          };
          
          const sliderValue = value != null ? getPos(value) : sortedMarks[0]?.position || 0;
          
          return (
            <Form.Item label={<span>{field.label}{renderSettingsIcon()}</span>}>
              <Slider
                min={0}
                max={maxPos}
                step={step}
                marks={marks}
                value={sliderValue}
                onChange={(pos) => handleChange(getValue(pos))}
                disabled={disabled}
                tooltip={{ formatter: (pos) => String(getValue(pos)) }}
              />
            </Form.Item>
          );
        }
        
        // Numeric mode
        const marks = config.showMarks ? {
          [config.min]: config.minLabel || config.min,
          [config.max]: config.maxLabel || config.max
        } : undefined;
        const numValue = typeof value === 'number' ? value : config.min;
        
        return (
          <Form.Item label={<span>{field.label}{renderSettingsIcon()}</span>}>
            <Slider
              min={config.min}
              max={config.max}
              step={config.step}
              marks={marks}
              value={numValue}
              onChange={handleChange}
              disabled={disabled}
              tooltip={{ formatter: (val) => String(val) }}
            />
          </Form.Item>
        );
      }

      default:
        return (
          <div>
            <Form.Item label={field.label}>
              <Input
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                disabled={disabled}
              />
            </Form.Item>
          </div>
        );
    }
  };

  return (
    <div className="form-field" data-field-id={field.id} data-snomed-code={field.conceptMapping?.code}>
      {renderField()}
      {field.conceptMapping && (
        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
          SNOMED: {field.conceptMapping.code} - {field.conceptMapping.display}
        </div>
      )}
    </div>
  );
};

export default FormField;

